import api, { getSialApiHost } from '../api.js';
import db from '../../versa/db.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import * as types from '../types.js';

const PRODUCT_HELP_BODY_CLASS = 'product-help-body';

const ICO_BOX = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5M12 22V12"/></svg>';
const ICO_BAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 7h16M4 12h10M4 17h13"/></svg>';
const ICO_SCALE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 3v18M5 7h14"/><path d="M5 7 2 13h6L5 7zm14 0-3 6h6l-3-6z"/></svg>';
const ICO_CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';

/**
 * Extrai cClassTrib (6 alfanuméricos) do valor interno ou do rótulo do datalist.
 * @param {unknown} raw
 * @returns {string}
 */
function normalizeClassTribCode(raw)
{
	const s = String(raw ?? '').trim().toUpperCase();
	if (!s || s === '-1' || s === '-2')
		return '';
	const m = s.match(/^([A-Z0-9]{6})\b/) || s.match(/^([A-Z0-9]{6})/);
	return m ? m[1] : '';
}

/**
 * Cabeçalho e cards do cadastro de produto.
 * @param {HTMLElement|null} root
 */
function syncProductPanel(root)
{
	if (!root)
		return;
	const val = (sel) =>
	{
		const el = root.querySelector(sel);
		return el ? String(el.value ?? '').trim() : '';
	};
	const setText = (sel, text) =>
	{
		root.querySelectorAll(sel).forEach((el) =>
		{
			el.textContent = text;
		});
	};
	setText('[data-sdp="title"]', val('#name') || 'Novo produto');
	setText('[data-sdp="sku"]', val('#sku') || '—');
	setText('[data-sdp="kpi-sku"]', val('#sku') || '—');
	setText('[data-sdp="kpi-qty"]', val('#quantity') || '0');
	setText('[data-sdp="kpi-value"]', val('#value') || '0');
	setText('[data-sdp="kpi-life"]', val('#shelf_life') || '0');
	const badgeOff = root.querySelector('[data-sdp="badge-disabled"]');
	const badgeCold = root.querySelector('[data-sdp="badge-refrigerated"]');
	if (badgeOff)
		badgeOff.hidden = !root.querySelector('#is_disabled')?.checked;
	if (badgeCold)
		badgeCold.hidden = !root.querySelector('#is_refrigerated')?.checked;
}

const PRODUCT_HELP_DADOS_HTML = `
<div class="${PRODUCT_HELP_BODY_CLASS}">
<h4>Empresa</h4>
<p>Empresa (depositante) à qual o produto pertence. Define escopo de IDs sequenciais, categorias, unidades e demais cadastros filhos.</p>
<h4>Produto</h4>
<p>Identificador numérico interno do item; preenchido após a primeira gravação.</p>
<h4>Categoria e fabricante</h4>
<p>Categoria regras de SKU/taxonomia e classificação comercial. Fabricante (laboratório/fornecedor industrial) usado em cadastro e relatórios.</p>
<h4>Un Compra / Un Venda</h4>
<p>Unidade de medida padrão na compra e na venda (ex.: CX, UN). Podem coincidir ou não conforme o negócio.</p>
<h4>SKU e taxonomia</h4>
<p>SKU é o código interno do produto (tamanho e formato dependem da categoria). Taxonomia complementa a chave de identificação comercial no SIAL.</p>
<h4>Nome, descrição, sinônimos e referências</h4>
<p>Nome exibido e buscas; sinônimos e referências ajudam localização e integrações (códigos externos).</p>
<h4>Código cliente</h4>
<p>Código do produto na base do cliente (DE/PARA para integração ou conferência).</p>
<h4>Programa</h4>
<p>Vínculo opcional a programa (ex.: programas de saúde): usado quando a empresa controla estoque ou políticas por programa.</p>
<h4>GTIN, NCM, CEST e origem</h4>
<p>GTIN (EAN) do produto ou “SEM GTIN” quando não houver código de barras (campo legado, ainda usado em NF-e e integrações). GTIN por nível de embalagem (P/S/T) e fabricante vinculado ficam em <strong>Catálogo → Embalagens de produto</strong>; os dois podem coexistir durante a migração.</p>
<h4>Medicamentos (Anvisa, justificativa, PMC)</h4>
<p>Campos opcionais para registro/justificativa junto à Anvisa e preço máximo ao consumidor, quando aplicável ao item.</p>
<h4>Inativo e imagem</h4>
<p>Inativo impede uso operacional normal do cadastro. Imagem associada ao produto (arquivo PNG) para exibição ou documentação.</p>
</div>`;

const PRODUCT_HELP_ARMAZENAGEM_HTML = `
<div class="${PRODUCT_HELP_BODY_CLASS}">
<h4>Vida (prazo de validade mínimo)</h4>
<p>Prazo mínimo de validade em dias exigido para o produto em operações que respeitam essa regra (ex.: recebimento ou picking).</p>
<h4>Est Mínimo, Estoque e Valor</h4>
<p><strong>Est Mínimo:</strong> referência de reposição.</p>
<p><strong>Estoque e Valor</strong> no cadastro são campos no registro do <em>produto</em> (tabela <code>products</code>): total e valor agregados para a grade e para cálculos (ex.: custo médio em transações). Eles <strong>não</strong> geram carga inicial operacional: não criam lote, não criam posição e não gravam na tabela <code>stocks</code>. O saldo “de verdade” do armazém vem de <strong>entradas</strong> (pedido/conferência/estocagem), <strong>movimentações</strong> e <strong>inventário</strong>. Em operação normal, quantidade e valor do produto são atualizados pelo serviço de <strong>transações</strong> quando há entrada ou saída física do armazém.</p>
<h4>Portaria 344</h4>
<p>Indica se o item se enquadra na Portaria 344/98 e, se sim, a lista (A1, B1, C1…). Afeta tratamento de medicamentos controlados.</p>
<h4>Refrigerado</h4>
<p>Marque quando o produto exige armazenamento ou transporte refrigerado. A informação aparece na descrição operacional (picking, NF-e) como qualificador «REFRIGERADO».</p>
<h4>Countback na separação (Coletor)</h4>
<p>Quando <strong>ligado</strong>, após o separador confirmar a quantidade deste SKU no <strong>Coletor</strong>, o sistema exige informar o <strong>saldo restante na posição</strong> (o que sobrou, não o que foi retirado). O SIAL compara com o saldo esperado. Se divergir, o operador pode tentar de novo (até <strong>3</strong> tentativas); na terceira, a linha fica bloqueada para auditoria. O gestor libera em <strong>Movimentação → Countback Picking</strong>, com observação obrigatória. Quando <strong>desligado</strong> (padrão), a separação deste produto segue sem essa validação. O parâmetro é <strong>por produto (SKU)</strong>, não por empresa.</p>
<h4>Hierarquia de embalagens (P / S / T)</h4>
<p>Tabela somente leitura das embalagens cadastradas em <strong>Embalagens de produto</strong> (níveis primário, secundário, terciário). Use <strong>Atualizar lista</strong> para recarregar sem fechar o formulário. A empresa precisa ter <strong>Hierarquia de embalagens</strong> ativa para o modelo novo nas operações.</p>
<h4>Dimensões ES / ET (legado)</h4>
<p>Medidas físicas gravadas no próprio produto: ES = secundária, ET = terciária (campos <code>master_*</code> na API). Complementam — não substituem — o cadastro de embalagens por nível.</p>
<h4>Classes</h4>
<p>Associação do produto a classes operacionais/fiscais da empresa. Use as setas para incluir ou remover da lista “Classes” a partir de “Disponíveis”.</p>
</div>`;

const PRODUCT_HELP_TRIBUTACAO_HTML = `
<div class="${PRODUCT_HELP_BODY_CLASS}">
<h4>Mono (monofásico)</h4>
<p>Indica tributação monofásica quando aplicável ao produto (PIS/COFINS ou ICMS conforme parametrização fiscal do ambiente).</p>
<h4>ST (substituição tributária)</h4>
<p>Marcar quando o item estiver sujeito a ST na operação da empresa.</p>
<h4>Isento</h4>
<p>Indica isenção de ICMS para o produto nas regras em que o SIAL utiliza esse flag.</p>
<h4>ICMS</h4>
<p>Alíquota ou percentual específico de ICMS do produto quando houver tratamento diferenciado em relação à regra padrão da empresa/nota.</p>
</div>`;

const PRODUCT_HELP_BY_TAB = {
	dados: { title: 'Ajuda — Dados do produto', html: PRODUCT_HELP_DADOS_HTML },
	armazenagem: { title: 'Ajuda — Armazenagem', html: PRODUCT_HELP_ARMAZENAGEM_HTML },
	tributacao: { title: 'Ajuda — Tributação', html: PRODUCT_HELP_TRIBUTACAO_HTML },
};

export default {

	html: `
		<style>
			#product .form-section { margin-bottom: 1.5rem; }
			#product label.checkbox-row { display: flex; align-items: center; gap: 0.5rem; min-height: 2.25rem; }
			#product label.checkbox-row input[type="checkbox"] { width: 1.25rem; height: 1.25rem; min-width: 1.25rem; min-height: 1.25rem; margin: 0; cursor: pointer; accent-color: #6d28d9; }
			#product input[type="checkbox"] { width: 1.25rem; height: 1.25rem; min-width: 1.25rem; min-height: 1.25rem; margin: 0; cursor: pointer; accent-color: #6d28d9; }
			#product .sku					{ width: 25%; 		}
			#product .taxons				{ width: 75%; 		}
			#product #image					{ width: auto; max-width: 18rem; display: inline-block;	}
			#product .product-tab-help {
				display: inline-flex; align-items: center; justify-content: center;
				width: 1.35rem; height: 1.35rem; padding: 0;
				border: 1px solid #c4b5fd; border-radius: 999px;
				background: #fff; color: #6d28d9;
				font-size: 0.85rem; font-weight: 700; line-height: 1;
				cursor: pointer; flex-shrink: 0;
			}
			#product .product-tab-help:hover { background: #f3e8ff; }
			#product .product-tab-help:focus { outline: 2px solid #7c3aed; outline-offset: 2px; }
			#product .product-pkg-hint { font-size: 0.8rem; color: #64748b; line-height: 1.45; max-width: 100%; margin: 0 0 0.5rem 0; }
			#product .product-pkg-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem 1rem; margin-top: 0.35rem; }
			#product .product-pkg-actions .button { white-space: nowrap; }
			#product .product-pkg-embed-status { font-size: 0.8rem; color: #64748b; min-height: 1.25rem; margin: 0.25rem 0 0.35rem 0; }
			#product .product-pkg-table-wrap { overflow: auto; max-height: 16rem; border: 1px solid var(--color-border); border-radius: 6px; margin-top: 0.25rem; background: #fff; }
			#product .product-pkg-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
			#product .product-pkg-table th, #product .product-pkg-table td { padding: 0.35rem 0.55rem; text-align: left; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
			#product .product-pkg-table th { background: #f5f3ff; font-weight: 600; white-space: nowrap; position: sticky; top: 0; z-index: 1; }
			#product .product-pkg-table tr:last-child td { border-bottom: none; }
			#product .product-pkg-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
			#product .product-pkg-table .cell-mono { font-family: ui-monospace, monospace; font-size: 0.78rem; }
		</style>
		<style>
			.product-help-body { max-height: 65vh; overflow-y: auto; text-align: left; }
			.product-help-body h4 {
				margin: 0.85rem 0 0.25rem 0;
				font-size: 0.78rem; font-weight: 700; text-transform: uppercase;
				letter-spacing: 0.03em; color: #1f2937;
			}
			.product-help-body h4:first-child { margin-top: 0; }
			.product-help-body p {
				margin: 0; font-size: 0.875rem; line-height: 1.45; color: #374151;
			}
		</style>
		<form id="product" class="form sial-modal-form sial-detail sial-detail--edit sial-detail--tabs" autocomplete="off">
			<datalist id="companies"></datalist>
			<datalist id="categories"></datalist>
			<datalist id="units"></datalist>
			<datalist id="manufacturers"></datalist>
			<datalist id="origins"></datalist>
			<datalist id="programs"></datalist>
			<datalist id="class_tribs"></datalist>

			<header class="sdp-head">
				<div class="sdp-head-main">
					<div class="sdp-icon">${ICO_BOX}</div>
					<div class="sdp-head-copy">
						<p class="sdp-kicker">Cadastro de produto</p>
						<h2 class="sdp-title" data-sdp="title">Novo produto</h2>
						<div class="sdp-meta">
							<span>SKU: <b data-sdp="sku">—</b></span>
							<span class="sdp-badge sdp-badge--blocked" data-sdp="badge-disabled" hidden>Inativo</span>
							<span class="sdp-badge sdp-badge--other" data-sdp="badge-refrigerated" hidden>Refrigerado</span>
						</div>
					</div>
				</div>
				<button type="button" class="bn_Escape sdp-close" title="Fechar" aria-label="Fechar">×</button>
			</header>
			<section class="sdp-kpis" aria-label="Resumo do produto">
				<div class="sdp-kpi sdp-kpi--total">
					<div class="sdp-kpi-label">${ICO_BAR} SKU</div>
					<div class="sdp-kpi-value sdp-kpi-value--sm"><span data-sdp="kpi-sku">—</span></div>
				</div>
				<div class="sdp-kpi sdp-kpi--avail">
					<div class="sdp-kpi-label">${ICO_BOX} Estoque</div>
					<div class="sdp-kpi-value"><span data-sdp="kpi-qty">0</span><span class="sdp-kpi-unit">un</span></div>
				</div>
				<div class="sdp-kpi sdp-kpi--reserved">
					<div class="sdp-kpi-label">${ICO_SCALE} Valor</div>
					<div class="sdp-kpi-value sdp-kpi-value--sm"><span data-sdp="kpi-value">0</span></div>
				</div>
				<div class="sdp-kpi sdp-kpi--free">
					<div class="sdp-kpi-label">${ICO_CLOCK} Vida</div>
					<div class="sdp-kpi-value"><span data-sdp="kpi-life">0</span><span class="sdp-kpi-unit">dias</span></div>
				</div>
			</section>

			<div class="form-tabs">
				<div class="form-tab-wrap">
					<button type="button" class="form-tab active" data-tab="dados">Dados do produto</button>
					<button type="button" class="product-tab-help" data-help="dados" title="Ajuda: campos desta aba" aria-label="Ajuda — Dados do produto">?</button>
				</div>
				<div class="form-tab-wrap">
					<button type="button" class="form-tab" data-tab="armazenagem">Armazenagem</button>
					<button type="button" class="product-tab-help" data-help="armazenagem" title="Ajuda: campos desta aba" aria-label="Ajuda — Armazenagem">?</button>
				</div>
				<div class="form-tab-wrap">
					<button type="button" class="form-tab" data-tab="tributacao">Tributação</button>
					<button type="button" class="product-tab-help" data-help="tributacao" title="Ajuda: campos desta aba" aria-label="Ajuda — Tributação">?</button>
				</div>
			</div>
			<div class="form-tab-panels">
				<div id="tab-dados" class="form-tab-panel active">
					<div class="form-section">
						<div class="form-section-title">Dados básicos</div>
						<div class="form-grid form-grid-6">
							<label title="Empresa">Empresa<sup>*</sup><input id="company_id" type="text" list="companies" required></label>
							<label title="Identificador do produto">Produto<input id="id" type="text" disabled></label>
							<label title="Categoria">Categoria<sup>*</sup><input id="category_id" type="text" list="categories" required></label>
							<label title="Fabricante">Fabricante<sup>*</sup><input id="manufacturer_id" type="text" list="manufacturers" required></label>
							<label title="Unidade de compra">Un Compra<sup>*</sup><input id="purchasing_unit_id" type="text" list="units" required></label>
							<label title="Unidade de venda">Un Venda<sup>*</sup><input id="selling_unit_id" type="text" list="units" required></label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Identificação e descrição</div>
						<div class="fsb">
							<fieldset class="sku"><legend>SKU<sup>*</sup></legend><table><tr><td><input id="sku" type="text" required title="Código do produto."></td></tr></table></fieldset>
							<fieldset class="taxons"><legend>Taxonomia<sup>*</sup></legend><table><tr><td><input id="taxons" type="text" required></td></tr></table></fieldset>
						</div>
						<div class="form-grid form-grid-2 mtm">
							<label title="Nome">Nome<sup>*</sup><input id="name" type="text" required></label>
							<label title="Descrição">Descrição<textarea id="description" rows="2"></textarea></label>
						</div>
						<div class="form-grid form-grid-4 mtm">
							<label title="Sinônimos">Sinônimos<input id="synonyms" type="text"></label>
							<label title="Referências">Referências<input id="reference" type="text"></label>
							<label title="Código cliente">Código cliente<input id="codigo_cliente" type="text"></label>
							<label title="Programa">Programa<input id="program_id" type="text" list="programs"></label>
						</div>
						<div class="form-grid form-grid-4 mtm">
							<label title="GTIN">GTIN<sup>*</sup><input id="gtin" type="text" required placeholder="SEM GTIN"></label>
							<label title="NCM">NCM<sup>*</sup><input id="ncm" type="text" required></label>
							<label title="CEST">CEST<input id="cest" type="text"></label>
							<label title="Origem">Origem<sup>*</sup><input id="origin" type="text" list="origins" required></label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Medicamentos</div>
						<fieldset>
							<label title="Anvisa">Anvisa<input id="drug_anvisa" type="text"></label>
							<label title="Justificativa ISENTO">Justificativa<input id="drug_exemption" type="text"></label>
							<label title="PMC">PMC<input id="drug_max_price" type="text"></label>
							<label title="Código de benefício fiscal NF-e SP (ex.: SP010920 ou SEM CBENEF)">cBenef SP<input id="c_benef" type="text" placeholder="SP010920 / SEM CBENEF" maxlength="12"></label>
						</fieldset>
					</div>
					<div class="form-section">
						<div class="form-section-title">Status</div>
						<div class="status-group">
							<label class="checkbox-row" title="Inativo"><input id="is_disabled" type="checkbox"> Inativo</label>
							<label class="file"><a id="download" download="image.png">Imagem</a><input id="image" type="file" accept=".png, image/x-png" title="Imagem"></label>
						</div>
					</div>
				</div>
				<div id="tab-armazenagem" class="form-tab-panel">
					<div class="form-section">
						<div class="form-section-title">Estoque e validade</div>
						<div class="form-grid form-grid-4">
							<label title="Validade mínima em dias">Vida<sup>*</sup><input id="shelf_life" type="text" required></label>
							<label title="Estoque mínimo desejado (referência)">Est Mínimo<input id="minimum_quantity" type="text"></label>
							<label title="Total gravado no cadastro do produto (grade). Não cria estoque por posição/lote — use Entrada / movimentações para carga inicial operacional.">Estoque (cadastro)<input id="quantity" type="text"></label>
							<label title="Valor do estoque">Valor<input id="value" type="text"></label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Portaria 344</div>
						<div class="status-group">
							<label class="checkbox-row" title="Produto sujeito à Portaria 344/98"><input id="is_portaria_344" type="checkbox"> Portaria 344</label>
							<label title="Lista 344"><select id="portaria_344_list"><option value="">—</option><option value="A1">A1</option><option value="A2">A2</option><option value="B1">B1</option><option value="C1">C1</option><option value="C2">C2</option><option value="C3">C3</option><option value="C4">C4</option><option value="C5">C5</option></select></label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Refrigerado</div>
						<div class="status-group">
							<label class="checkbox-row" title="Produto refrigerado — armazenamento/transporte; aparece como «REFRIGERADO» na descrição operacional"><input id="is_refrigerated" type="checkbox"> Refrigerado</label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Countback</div>
						<div class="status-group">
							<label class="checkbox-row" title="Após confirmar a separação deste SKU no Coletor, exige informar o saldo restante na posição (Countback). Após 3 erros, bloqueia para auditoria."><input id="out_enable_countback" type="checkbox"> Countback na separação (Coletor)</label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Hierarquia de embalagens (P / S / T)</div>
						<p class="product-pkg-hint">Embalagens já cadastradas para <strong>este produto</strong> aparecem na tabela abaixo (somente leitura). Para <strong>incluir ou alterar</strong> níveis, código, fator, GTIN etc., use depois <strong>Catálogo → Embalagens de produto</strong> — você não precisa sair deste modal só para consultar a lista. Ative <strong>Hierarquia de embalagens</strong> na empresa para o modelo valer nas operações.</p>
						<div class="product-pkg-actions">
							<button type="button" class="button bn_refresh_product_packagings" title="Recarregar embalagens da API">Atualizar lista</button>
						</div>
						<div class="product-pkg-embed-status" id="product_packagings_embed_status" aria-live="polite"></div>
						<div class="product-pkg-table-wrap">
							<table class="product-pkg-table">
								<thead>
									<tr>
										<th>ID</th>
										<th>SKU</th>
										<th>Código</th>
										<th>Nível</th>
										<th class="num">Fator</th>
										<th class="num">Cap. terc.</th>
										<th>Fabricante</th>
										<th>GTIN</th>
										<th>Padrão</th>
										<th>Inativa</th>
									</tr>
								</thead>
								<tbody id="product_packagings_embed_body"></tbody>
							</table>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Dimensões físicas legadas (ES / ET)</div>
						<p class="product-pkg-hint">Medidas no cadastro do <strong>produto</strong> (modelo antigo): <strong>ES</strong> = embalagem secundária (nível S), <strong>ET</strong> = embalagem terciária (nível T). No banco/API os campos da terciária continuam nomeados <code>master_*</code> por compatibilidade. Não substituem o cadastro hierárquico P/S/T em <strong>Embalagens de produto</strong>.</p>
						<div class="fsb">
							<fieldset><legend>Secundária (ES)</legend>
								<label title="Peso da embalagem secundária (kg)">Peso<input id="secondary_weight" type="text"></label>
								<label title="Altura da embalagem secundária (m)">Altura<input id="secondary_height" type="text"></label>
								<label title="Largura da embalagem secundária (m)">Largura<input id="secondary_width" type="text"></label>
								<label title="Comprimento da embalagem secundária (m)">Compr<input id="secondary_length" type="text"></label>
							</fieldset>
							<fieldset><legend>Terciária (ET)</legend>
								<label title="Peso da embalagem terciária (kg)">Peso<input id="master_weight" type="text"></label>
								<label title="Altura da embalagem terciária (m)">Altura<input id="master_height" type="text"></label>
								<label title="Largura da embalagem terciária (m)">Largura<input id="master_width" type="text"></label>
								<label title="Comprimento da embalagem terciária (m)">Compr<input id="master_length" type="text"></label>
							</fieldset>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">Classificações</div>
						<div class="fsb">
							<label class="keymap">Classes<select id="classes" multiple size="4"></select></label>
							<span class="keymap"><button type="button" class="bn_Left" title="Incluir">&#x1F844;</button><button type="button" class="bn_Right" title="Remover">&#x1F846;</button></span>
							<label class="keymap">Disponíveis<select id="available" multiple size="4"></select></label>
						</div>
					</div>
				</div>
				<div id="tab-tributacao" class="form-tab-panel">
					<div class="form-section">
						<div class="form-section-title">Impostos</div>
						<div class="form-grid form-grid-4">
							<label class="checkbox-row" title="Monofásico"><input id="is_mono" type="checkbox"> Mono</label>
							<label class="checkbox-row" title="Substituto Tributário"><input id="is_st" type="checkbox"> ST</label>
							<label class="checkbox-row" title="Isento de ICMS"><input id="is_exempt" type="checkbox"> Isento</label>
							<label title="Alíquota ICMS">ICMS<input id="icms" type="text"></label>
						</div>
					</div>
					<div class="form-section">
						<div class="form-section-title">IBS / CBS (ClassTrib)</div>
						<div class="form-grid form-grid-4">
							<label title="cClassTrib na NF-e — tabela oficial NT 2025.002">ClassTrib<input id="class_trib" type="text" maxlength="120" list="class_tribs" placeholder="000001" autocomplete="off"></label>
							<label title="CST IBS/CBS derivado do ClassTrib (tabela oficial; não grava — só exibição)">CST IBS/CBS<input id="class_trib_cst" type="text" maxlength="3" readonly tabindex="-1" placeholder="—"></label>
							<label title="Base legal do ClassTrib">Base legal<input id="class_trib_legal_basis" type="text" maxlength="200"></label>
							<label title="Nome / descrição do ClassTrib na tabela oficial (não grava — só exibição)">Descrição ClassTrib<input id="class_trib_name" type="text" readonly tabindex="-1" placeholder="—"></label>
							<label title="Alíquota IBS (%)">IBS %<input id="ibs_rate" type="text"></label>
							<label title="Alíquota CBS (%)">CBS %<input id="cbs_rate" type="text"></label>
						</div>
					</div>
				</div>
			</div>
			<div class="sdp-foot">
				<button type="button" class="button bn_product_disable btn-product-danger" title="Inativar produto">Inativar</button>
				<button type="button" class="button bn_product_enable" title="Ativar produto">Ativar</button>
				<button type="button" class="bn_Escape button">Fechar</button>
				<button type="button" class="bn_Enter button btn-product-success">Salvar</button>
			</div>
		</form>
	`,

	form: 
	{
		selector: '#product',
		init(formset)
		{
			Object.getPrototypeOf(this).init.call(this);
			const root = () => formset.dialog?.getNode?.();
			/**
			 * Garante código ClassTrib (6 chars) no sessionStorage do campo —
			 * evita rótulo do datalist / -1 no Salvar e destrava o CST.
			 * @returns {string}
			 */
			const forceClassTribCode = () =>
			{
				const field = formset.form?.getFieldBySelector?.('#class_trib');
				const code = normalizeClassTribCode(field?.getValue?.())
					|| normalizeClassTribCode(root()?.querySelector?.('#class_trib')?.value);
				if (code && field)
				{
					const cur = String(field.getValue?.() ?? '');
					if (cur !== code)
					{
						field.setValue(code);
						field.refresh?.();
					}
				}
				return code;
			};
			const syncClassTrib = async () => {
				const code = forceClassTribCode();
				const cstEl = root()?.querySelector?.('#class_trib_cst');
				const nameEl = root()?.querySelector?.('#class_trib_name');
				const legalField = formset.form?.getFieldBySelector?.('#class_trib_legal_basis');
				const legalEl = root()?.querySelector?.('#class_trib_legal_basis');
				if (!code) {
					if (cstEl) cstEl.value = '';
					if (nameEl) nameEl.value = '';
					return;
				}
				if (cstEl) cstEl.value = code.slice(0, 3);
				try {
					const row = await db.get('class_tribs', code);
					if (nameEl) nameEl.value = row?.name || '';
					if (row?.cst && cstEl) cstEl.value = String(row.cst);
					const legalEmpty = !String(legalField?.getValue?.() ?? legalEl?.value ?? '').trim();
					if (legalEmpty && row?.name) {
						if (legalField?.setValue)
						{
							legalField.setValue(row.name);
							legalField.refresh?.();
						}
						else if (legalEl)
							legalEl.value = row.name;
					}
				} catch (_) {
					if (nameEl) nameEl.value = '';
				}
			};
			const orig = this.setValues.bind(this);
			this.setValues = (values) =>
			{
				orig(values);
				syncProductPanel(this.node);
				syncClassTrib();
			};
			const origLoadList = formset.loadList.bind(formset);
			formset.loadList = async (datalist, store, options) =>
			{
				await origLoadList(datalist, store, options);
				if (store === 'class_tribs' || String(datalist) === '#class_tribs')
					await syncClassTrib();
			};
			const origRefresh = formset.form.refresh.bind(formset.form);
			formset.form.refresh = (...args) =>
			{
				origRefresh(...args);
				syncClassTrib();
			};
			const origSyncFromDom = formset.form.syncFromDom.bind(formset.form);
			formset.form.syncFromDom = () =>
			{
				origSyncFromDom();
				forceClassTribCode();
			};
			const origGetValues = formset.form.getValues.bind(formset.form);
			formset.form.getValues = () =>
			{
				const data = origGetValues();
				const code = forceClassTribCode() || normalizeClassTribCode(data.class_trib);
				if (code)
					data.class_trib = code;
				else
					delete data.class_trib;
				const legalDom = String(root()?.querySelector?.('#class_trib_legal_basis')?.value || '').trim();
				if (legalDom && !data.class_trib_legal_basis)
					data.class_trib_legal_basis = legalDom;
				return data;
			};
			formset.loadList('#companies', 'companies', { labels: ['nick'] });
			formset.loadList('#class_tribs', 'class_tribs', { labels: ['code', 'name'], value: 'code' });
			root()?.addEventListener?.('input', () => syncProductPanel(root()));
			root()?.addEventListener?.('change', () => syncProductPanel(root()));
			syncProductPanel(this.node);
			root()?.addEventListener?.('change', (ev) => { if (ev.target?.matches?.('#class_trib')) syncClassTrib(); });
			root()?.addEventListener?.('input', (ev) => { if (ev.target?.matches?.('#class_trib')) syncClassTrib(); });
			setTimeout(syncClassTrib, 100);
			setTimeout(syncClassTrib, 350);
			const getId = () => {
				const stored = formset.form.getFieldBySelector('#id')?.getValue?.();
				const fromField = parseInt(String(stored ?? '').replace(/\D/g, ''), 10);
				if (Number.isFinite(fromField) && fromField > 0)
					return String(fromField);
				const raw = String(root()?.querySelector?.('#id')?.value || '');
				const fromDom = parseInt(raw.replace(/\D/g, ''), 10);
				return Number.isFinite(fromDom) && fromDom > 0 ? String(fromDom) : '';
			};
			const getCompanyId = () => formset.form.getFieldBySelector('#company_id')?.getValue?.() ?? root()?.querySelector?.('#company_id')?.value;
			const loadEmbeddedPackagings = async () => {
				const statusEl = root()?.querySelector('#product_packagings_embed_status');
				const tbody = root()?.querySelector('#product_packagings_embed_body');
				if (!statusEl || !tbody) return;
				const company_id = String(getCompanyId() || '').trim();
				const pid = String(getId() || '').replace(/\s/g, '');
				if (!company_id) {
					statusEl.textContent = 'Selecione a empresa para carregar embalagens.';
					tbody.innerHTML = '';
					return;
				}
				if (!pid || !/^\d+$/.test(pid)) {
					statusEl.textContent = 'Salve o produto ao menos uma vez para exibir as embalagens (P/S/T) vinculadas a ele.';
					tbody.innerHTML = '';
					return;
				}
				statusEl.textContent = 'Carregando embalagens…';
				tbody.innerHTML = '';
				const host = getSialApiHost();
				const path = `/companies/${encodeURIComponent(company_id)}/products/${encodeURIComponent(pid)}/packagings`;
				const url = (host && String(host).trim()) ? `${String(host).replace(/\/$/, '')}${path}` : path;
				const esc = (x) => String(x ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
				const lvl = (l) => ({ P: 'P — Primário', S: 'S — Secundário', T: 'T — Terciário' }[l] || esc(l));
				try {
					const r = await fetch(url, { credentials: 'include' });
					if (!r.ok) {
						statusEl.textContent = r.status === 403 ? 'Sem permissão para listar embalagens.' : `Não foi possível carregar (${r.status}).`;
						return;
					}
					const list = await r.json();
					if (!Array.isArray(list)) {
						statusEl.textContent = 'Resposta inválida da API.';
						return;
					}
					if (list.length === 0) {
						statusEl.textContent = 'Nenhuma embalagem cadastrada para este produto.';
						return;
					}
					statusEl.textContent = `${list.length} embalagem(ns) cadastrada(s).`;
					const fabLabel = async (mid) => {
						if (mid == null || mid === '') return '—';
						try {
							const rec = await db.get('manufacturers', [company_id, Number(mid)]);
							if (rec && (rec.nick || rec.name)) return esc(`${rec.id} ${rec.nick || rec.name}`);
						} catch (e) { /* ignore */ }
						return esc(String(mid));
					};
					const rowsHtml = await Promise.all(list.map(async (row) => {
						const fab = await fabLabel(row.manufacturer_id);
						return `
						<tr>
							<td class="num">${esc(row.id)}</td>
							<td class="cell-mono">${row.sku != null && String(row.sku).trim() !== '' ? esc(row.sku) : '—'}</td>
							<td><strong>${esc(row.code)}</strong></td>
							<td>${lvl(row.level)}</td>
							<td class="num">${esc(row.factor)}</td>
							<td class="num">${row.tertiary_capacity != null && row.tertiary_capacity !== '' ? esc(row.tertiary_capacity) : '—'}</td>
							<td>${fab}</td>
							<td class="cell-mono">${row.gtin ? esc(row.gtin) : '—'}</td>
							<td>${row.is_default ? 'Sim' : '—'}</td>
							<td>${row.is_disabled ? 'Sim' : '—'}</td>
						</tr>`;
					}));
					tbody.innerHTML = rowsHtml.join('');
				} catch (e) {
					statusEl.textContent = 'Falha ao carregar. Verifique a conexão com a API.';
					console.warn(e);
				}
			};
			const setDisabled = (v) => { const f = formset.form.getFieldBySelector('#is_disabled'); if (f) { f.setValue(v ? 1 : 0); f.refresh(); } };
			const doAction = async (payload) => {
				const company_id = getCompanyId(), id = getId();
				if (!company_id || !id) return;
				const resp = await api.call(api.patch_product, company_id, id, payload);
				if (resp != null) setDisabled(payload.is_disabled);
				else notify('Falha ao atualizar produto.', 'Produtos');
			};
			root()?.addEventListener?.('click', (ev) => {
				if (ev.target?.closest?.('.bn_product_disable')) { ev.preventDefault(); ev.stopPropagation(); doAction({ is_disabled: true }); return; }
				if (ev.target?.closest?.('.bn_product_enable')) { ev.preventDefault(); ev.stopPropagation(); doAction({ is_disabled: false }); return; }
				const helpEl = ev.target?.closest?.('.product-tab-help');
				if (helpEl) {
					ev.preventDefault();
					ev.stopPropagation();
					const key = helpEl.getAttribute('data-help');
					const pack = PRODUCT_HELP_BY_TAB[key];
					if (!pack) return;
					let mb;
					mb = msgbox({
						title: pack.title,
						text: pack.html,
						buttons: [{ label: 'Fechar', cls: 'bn_Enter' }],
						callback: () => mb.destroy(),
					});
					return;
				}
				if (ev.target?.closest?.('.bn_refresh_product_packagings')) {
					ev.preventDefault();
					ev.stopPropagation();
					loadEmbeddedPackagings();
					return;
				}
				const tab = ev.target?.closest?.('.form-tab');
				if (!tab) return;
				ev.preventDefault();
				const id = tab.getAttribute('data-tab');
				root()?.querySelectorAll?.('.form-tab').forEach(t => t.classList.remove('active'));
				root()?.querySelectorAll?.('.form-tab-panel').forEach(p => p.classList.remove('active'));
				tab.classList.add('active');
				const panel = root()?.querySelector?.(`#tab-${id}`);
				if (panel) panel.classList.add('active');
				if (id === 'armazenagem')
					loadEmbeddedPackagings();
			});
			const syncPortaria344 = () => {
				const cb = root()?.querySelector?.('#is_portaria_344');
				const sel = root()?.querySelector?.('#portaria_344_list');
				if (cb && sel) { sel.disabled = !(cb.checked || cb.value === '1'); }
			};
			root()?.addEventListener?.('change', (ev) => { if (ev.target?.matches?.('#is_portaria_344')) syncPortaria344(); });
			formset.form?.subscribe?.(['load', 'refresh'], syncPortaria344);
			formset.form?.subscribe?.(['load', 'refresh'], () => { loadEmbeddedPackagings(); });
			formset.form?.subscribe?.(['load', 'refresh'], () => { syncProductPanel(this.node); });
			setTimeout(syncPortaria344, 100);
			setTimeout(() => { loadEmbeddedPackagings(); }, 200);
			setTimeout(() => { syncProductPanel(this.node); }, 400);
		},
		afterEditSetValues(formset) {
			const panel = formset.form.node;
			syncProductPanel(panel);
			setTimeout(() => syncProductPanel(panel), 400);
		},
	},
	
	fields: [
		{ properties: { selector: '#company_id' }, types: [types.pubsub(), types.datalist()] },
		{ properties: { selector: '#id' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#category_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('categories')] },
		{ properties: { selector: '#purchasing_unit_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('units')] },
		{ properties: { selector: '#selling_unit_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('units')] },
		{ properties: { selector: '#manufacturer_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('manufacturers', { labels: ['nick', 'name'] })] },
		//{ properties: { selector: '#sku' }, types: [types.pubsub(), types.observer('category_id'), types.obs_sku('.sku', '.taxons')] },
		//{ properties: { selector: '#sku' }, types: [types.sku(6)] },
		{ properties: { selector: '#sku' }, types: [types.pubsub(), types.observer('category_id'), types.obs_set_sku_type()] },
		{ properties: { selector: '#taxons' }, types: [types.name()] },
		{ properties: { selector: '#name' }, types: [types.name()] },
		{ properties: { selector: '#synonyms' }, types: [types.array(), types.name()] },
		{ properties: { selector: '#description' }, types: [types.textarea()] },
		{ properties: { selector: '#reference' }, types: [types.array()] },
		{ properties: { selector: '#codigo_cliente' }, types: [types.name()] },
		{ properties: { selector: '#program_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('programs', { labels: ['name'] })] },
		{ properties: { selector: '#gtin' }, types: [types.gtin(), types.caps()] },
		{ properties: { selector: '#ncm' }, types: [types.ncm()] },
		{ properties: { selector: '#cest' }, types: [types.cest()] },
		{ properties: { selector: '#origin' }, types: [types.opt_origin(), types.datalist()] },
		{ properties: { selector: '#is_mono' }, types: [types.checkbox()] },
		{ properties: { selector: '#is_st' }, types: [types.checkbox()] },
		{ properties: { selector: '#is_exempt' }, types: [types.checkbox()] },
		{ properties: { selector: '#icms' }, types: [types.number(2)] },
		{ properties: { selector: '#c_benef' }, types: [types.caps()] },
		{ properties: { selector: '#class_trib' }, types: [types.caps(), types.datalist()] },
		// CST / Descrição: só exibição (derivados do ClassTrib) — fora do formset para form.refresh() não apagar
		{ properties: { selector: '#class_trib_legal_basis' }, types: [] },
		{ properties: { selector: '#ibs_rate' }, types: [types.number(4)] },
		{ properties: { selector: '#cbs_rate' }, types: [types.number(4)] },
		{ properties: { selector: '#shelf_life' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#minimum_quantity' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#quantity' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#value' }, types: [types.number(2)] },
		//{ properties: { selector: '#drug_anvisa' }, types: [types.anvisa(), types.caps()] },
		//{ properties: { selector: '#drug_exemption' }, types: [] },
		//{ properties: { selector: '#drug_max_price' }, types: [types.number(2)] },
		{ properties: { selector: '#secondary_weight' }, types: [types.number(3)] },
		{ properties: { selector: '#secondary_height' }, types: [types.number(3)] },
		{ properties: { selector: '#secondary_width' }, types: [types.number(3)] },
		{ properties: { selector: '#secondary_length' }, types: [types.number(3)] },
		{ properties: { selector: '#master_weight' }, types: [types.number(3)] },
		{ properties: { selector: '#master_height' }, types: [types.number(3)] },
		{ properties: { selector: '#master_width' }, types: [types.number(3)] },
		{ properties: { selector: '#master_length' }, types: [types.number(3)] },
		{ properties: { selector: '#classes' }, types: [types.pubsub(), types.keymap('classes', '#available', { values: ['company_id', 'id'] }), types.observer('company_id'), types.obs_load_keymap()] },
		{ properties: { selector: '#image' }, types: [types.pubsub(), types.file()] },
		{ properties: { selector: '#is_portaria_344' }, types: [types.checkbox()] },
		{ properties: { selector: '#is_refrigerated' }, types: [types.checkbox()] },
		{ properties: { selector: '#out_enable_countback' }, types: [types.checkbox()] },
		{ properties: { selector: '#portaria_344_list' }, types: [types.pubsub()] },
		{ properties: { selector: '#is_disabled' }, types: [types.checkbox()] },
	]

};

